import { useTranslation } from 'react-i18next';

import { Button, Group, Stack, Text, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { closeModal } from '@mantine/modals';
import { handleSubmitError } from '@utils/forms';

import {
  Customer,
  UpdateCustomerRequest,
} from '@interfaces/customer.interface';

import { useUpdateCurrentCustomerMutation } from '@api/customer.api';

import {
  CitySelector,
  NationSelector,
  ProvinceSelector,
  ZipSelector,
} from './common/locations';

export const UPDATE_CUSTOMER_BILLING_ADDRESS_MODAL_ID =
  'update-customer-billing-address';

interface UpdateCustomerBillingAddressProps {
  customer: Customer;
}

export default function UpdateCustomerBillingAddress({
  customer,
}: UpdateCustomerBillingAddressProps) {
  const { t } = useTranslation();

  // ==========================================================================
  // Api
  // ==========================================================================
  const [updateCurrentCustomer, { isLoading }] =
    useUpdateCurrentCustomerMutation();

  // ==========================================================================
  // Form
  // ==========================================================================
  const initialValues = {
    address: customer.address || '',
    postalCode: customer.postalCode || '',
    city: customer.city || '',
    province: customer.province || '',
    country: customer.country || '',
  };

  const form = useForm({
    initialValues,
  });

  // ==========================================================================
  // Handlers
  // ==========================================================================
  const onSubmit = async (values: typeof initialValues) => {
    try {
      const mappedValues: UpdateCustomerRequest = {
        address: values.address,
        postalCode: values.postalCode,
        city: values.city,
        province: values.province,
        country: values.country,
      };

      await updateCurrentCustomer(mappedValues).unwrap();

      closeModal(UPDATE_CUSTOMER_BILLING_ADDRESS_MODAL_ID);
    } catch (error) {
      handleSubmitError(error, form);
    }
  };

  // ==========================================================================
  // Render
  // ==========================================================================
  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Stack>
        <NationSelector required {...form.getInputProps('country')} />

        {form.values.country === 'Italia' ? (
          <ProvinceSelector required {...form.getInputProps('province')} />
        ) : (
          <TextInput
            label={t('address.province')}
            placeholder={t('address.provincePlaceholder')}
            required
            {...form.getInputProps('province')}
          />
        )}
        {form.values.country === 'Italia' ? (
          <CitySelector
            province={form.values.province}
            disabled={form.values.province === ''}
            required
            {...form.getInputProps('city')}
          />
        ) : (
          <TextInput
            label={t('address.city')}
            placeholder={t('address.cityPlaceholder')}
            required
            {...form.getInputProps('city')}
          />
        )}

        {form.values.country === 'Italia' ? (
          <ZipSelector
            city={form.values.city}
            disabled={form.values.city === ''}
            required
            {...form.getInputProps('postalCode')}
          />
        ) : (
          <TextInput
            label={t('address.postalCode')}
            placeholder={t('address.postalCodePlaceholder')}
            required
            {...form.getInputProps('postalCode')}
          />
        )}
        <TextInput
          label={t('address.address')}
          placeholder={t('address.addressPlaceholder')}
          required
          {...form.getInputProps('address')}
        />
      </Stack>

      {form.errors.general && (
        <Text c="red" size="sm" ta="right" mt="lg">
          {form.errors.general}
        </Text>
      )}

      <Group justify="end" mt="lg">
        <Button
          variant="default"
          onClick={() => closeModal(UPDATE_CUSTOMER_BILLING_ADDRESS_MODAL_ID)}
          loading={isLoading}
        >
          {t('cart.cancel')}
        </Button>
        <Button type="submit" loading={isLoading}>
          {t('cart.confirm')}
        </Button>
      </Group>
    </form>
  );
}
